<!-- Toolbar action shown while at least one row is selected -->
<ng-template #processSelectedButton>
	<eif-custom-button
		[text]="'Process selected'"
		[preIcon]="'fa-solid fa-bolt'"
		[size]="'small'"
		(action)="processSelected()">
	</eif-custom-button>
</ng-template>

<!-- Toolbar action shown while nothing is selected -->
<ng-template #newDocumentButton>
	<eif-custom-button
		[text]="'New document'"
		[preIcon]="'fa-solid fa-plus'"
		[size]="'small'"
		(action)="createDocument()">
	</eif-custom-button>
</ng-template>

<!-- Row expand template (also reused as cardExpandTemplate) -->
<ng-template
	#rowExpand
	let-row>
	<div class="ct-expand">
		<div class="ct-expand__item">
			<span class="ct-expand__label">Id</span>
			<span class="ct-expand__value">{{ row.Id }}</span>
		</div>
		<div class="ct-expand__item">
			<span class="ct-expand__label">Document</span>
			<span class="ct-expand__value">{{ row.Type }} {{ row.Number }}</span>
		</div>
		<div class="ct-expand__item">
			<span class="ct-expand__label">Supplier</span>
			<span class="ct-expand__value">{{ row.Supplier?.Name }} ({{ row.Supplier?.CountryCode }}{{ row.Supplier?.VatNumber }})</span>
		</div>
		<div class="ct-expand__item">
			<span class="ct-expand__label">Buyer</span>
			<span class="ct-expand__value">{{ row.Buyer?.Name }} ({{ row.Buyer?.CountryCode }}{{ row.Buyer?.VatNumber }})</span>
		</div>
		<div class="ct-expand__item">
			<span class="ct-expand__label">Amount</span>
			<span class="ct-expand__value">{{ row.TotalAmount | currencyLocale: row.Currency }}</span>
		</div>
		<div class="ct-expand__item">
			<span class="ct-expand__label">Issued / Due</span>
			<span class="ct-expand__value">{{ row.CreationDate | dateLocale: 'yyyy/MM/dd' }} → {{ row.DueDate | dateLocale: 'yyyy/MM/dd' }}</span>
		</div>
		<div class="ct-expand__item">
			<span class="ct-expand__label">States</span>
			<span class="ct-expand__value">{{ row.ApprovalState }} / {{ row.PaymentState }}</span>
		</div>
		<div class="ct-expand__item">
			<span class="ct-expand__label">Selectable</span>
			<span class="ct-expand__value">{{ row.canSelect !== false ? 'Yes' : 'No (canSelect: false)' }}</span>
		</div>
	</div>
</ng-template>

<!-- Card template - rendered instead of the grid on narrow widths (or always, with cardViewOnly) -->
<ng-template
	#docCard
	let-row>
	<div class="eif-card">
		<div class="eif-card-header">
			<div class="is-bold truncate">{{ row.Number }}</div>
			<div>{{ row.Type }}</div>
		</div>
		<div class="eif-card-section">
			<div class="label">Supplier</div>
			<div class="is-bold">
				<div class="truncate">{{ row.Supplier?.Name }}</div>
				<div>{{ row.Supplier?.CountryCode }}{{ row.Supplier?.VatNumber }}</div>
			</div>
		</div>
		<div class="eif-card-section">
			<div class="label">Buyer</div>
			<div class="is-bold">
				<div class="truncate">{{ row.Buyer?.Name }}</div>
				<div>{{ row.Buyer?.CountryCode }}{{ row.Buyer?.VatNumber }}</div>
			</div>
		</div>
		<div class="eif-card-section">
			<div class="label">Amount</div>
			<div class="is-bold">{{ row.TotalAmount | currencyLocale: row.Currency }}</div>
		</div>
		<div class="eif-card-section">
			<div class="label">Issued</div>
			<div>{{ row.CreationDate | dateLocale: 'yyyy/MM/dd' }}</div>
		</div>
		<div class="eif-card-section">
			<div class="label">Due date</div>
			<div>{{ row.DueDate | dateLocale: 'yyyy/MM/dd' }}</div>
		</div>
		<!-- 'icons' columns are grid-view only - the card template renders the equivalent flags -->
		@if (isOverdue(row) || row.TotalAmount >= 25000) {
			<div class="eif-card-section">
				<div class="label">Flags</div>
				<div class="ct-card-icons">
					@if (isOverdue(row)) {
						<i
							class="fa-solid fa-triangle-exclamation is-color-danger ct-card-icons__item ct-card-icons__item--clickable"
							[eifTooltip]="'Overdue - due date has passed and it is still unpaid'"
							(click)="flagOverdue(row)">
						</i>
					}
					@if (row.TotalAmount >= 25000) {
						<i
							class="fa-solid fa-star is-color-warning ct-card-icons__item"
							[eifTooltip]="'High amount (>= 25,000)'">
						</i>
					}
				</div>
			</div>
		}
		<div class="eif-card-section">
			<div class="label">Approval</div>
			<div>
				<span class="tag-{{ approvalVariant(row) }}">{{ row.ApprovalState }}</span>
			</div>
		</div>
		<div class="eif-card-section">
			<div class="label">Payment</div>
			<div>
				<span class="tag-{{ paymentVariant(row) }}">{{ row.PaymentState }}</span>
			</div>
		</div>
	</div>
</ng-template>

<p class="ct-hint mb-3">
	<span class="inline-block align-items-center">
		<i class="fa-solid fa-circle-info"></i>
	</span>
	<span>
		Every action fires a toast notification. Full payloads - row objects, <span class="ds-code">searchRequested</span> and
		<span class="ds-code">selectionChange</span> events - are logged via <span class="ds-code">console.log</span>, so open DevTools to
		inspect them.
	</span>
</p>

<div class="ds-grid ds-grid--2">
	<!-- Data controls -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Data</h3>
			<p class="ds-example__header-subtitle">Regenerate the mock dataset - try 0 for the empty state or 100,000 to stress test the table</p>
		</div>
		<div class="ds-example__content">
			<eif-custom-input
				[label]="'Row count'"
				[type]="'number'"
				[value]="rowCount"
				[placeholder]="'Number of rows to generate'"
				[fullWidth]="true"
				(valueChange)="onRowCountChange($event)">
			</eif-custom-input>
			<div class="ct-presets">
				@for (preset of rowCountPresets; track preset) {
					<eif-custom-button
						[text]="preset | number"
						[size]="'small'"
						[variant]="'outlined'"
						[skipTranslate]="true"
						(action)="setRowCount(preset)">
					</eif-custom-button>
				}
			</div>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Rows:</div>
				<div class="ds-value__content">{{ rows.length | number }}</div>
			</div>
		</div>
	</div>

	<!-- State toggles -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Table state</h3>
			<p class="ds-example__header-subtitle">Toggle loading and force card view to see the alternate layouts</p>
		</div>
		<div class="ds-example__content">
			<div class="ct-toggles">
				<eif-custom-switch
					[label]="'Loading'"
					[checked]="isLoading"
					(switchChange)="isLoading = $event">
				</eif-custom-switch>
				<eif-custom-switch
					[label]="'Card view only'"
					[checked]="cardViewOnly"
					(switchChange)="cardViewOnly = $event">
				</eif-custom-switch>
			</div>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Selected:</div>
				<div class="ds-value__content">{{ selectedRows.length }} row(s)</div>
			</div>
		</div>
	</div>
</div>

<!-- The table itself -->
<div class="simple-card top-line ps-3 pe-3 mt-4">
	<eif-custom-table
		#customTable
		[columns]="tableColumns"
		[rows]="rows"
		[loading]="isLoading"
		[filters]="tableFilters"
		[enableFilters]="true"
		[selectionMode]="'multiple'"
		[persistSelection]="true"
		[idField]="'Id'"
		[selectActions]="selectActions"
		[toolbarActions]="toolbarActions"
		[showRefresh]="true"
		[export]="tableExport"
		[expandTemplate]="rowExpand"
		[cardTemplate]="docCard"
		[cardExpandTemplate]="rowExpand"
		[cardViewOnly]="cardViewOnly"
		[config]="tableConfig"
		(searchRequested)="onSearchRequested($event)"
		(selectionChange)="onSelectionChange($event)">
	</eif-custom-table>
</div>

<!-- Inputs deliberately not demoed -->
<div class="ds-example mt-4">
	<div class="ds-example__header">
		<h3 class="ds-example__header-title">Inputs not demoed</h3>
		<p class="ds-example__header-subtitle">
			These exist on <span class="ds-code">eif-custom-table</span> but are intentionally left out of this page
		</p>
	</div>
	<div class="ds-example__content">
		<ul class="ct-notes">
			<li>
				<span class="ds-code">identifier</span> / <span class="ds-code">pathPattern</span> - persist filters &amp; page size in localStorage
				per route. Pointless here: the design-system page is a single fixed path.
			</li>
			<li>
				<span class="ds-code">dataSource</span> / <span class="ds-code">searchCriteria</span> - server-side search mode (API-driven
				paging/filtering). This demo is fully client-side via <span class="ds-code">[rows]</span>.
			</li>
			<li>
				<span class="ds-code">sortColumn</span> / <span class="ds-code">sortDirection</span> - externally controlled sort state, for
				parent-managed or server-side tables.
			</li>
			<li><span class="ds-code">maxRecords</span> - caps server-side results and warns via toast; not meaningful on local data.</li>
			<li>
				<span class="ds-code">getRowId</span> - custom row identity function; we use <span class="ds-code">idField='Id'</span> instead.
			</li>
			<li><span class="ds-code">userTimeZone</span> - timezone for date filters; defaults to <span class="ds-code">'UTC'</span>.</li>
			<li>
				<span class="ds-code">type: 'icons'</span> column - rendered in grid view only; card view ignores it, so the card template above
				renders the equivalent flags manually.
			</li>
		</ul>
	</div>
</div>
